'use client';

import { useTranslations } from 'next-intl';
import { IconButton } from '@zillow/constellation-10';
import { Search } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';

type SearchSubmitProps = {
  size: 'sm' | 'lg';
  disabled?: boolean;
  isPending?: boolean;
};

/**
 * Search submit — circular brand IconButton at both sizes. Icon-only.
 * `type="submit"` so clicks and Enter share the surrounding form's single
 * submission path (ListingSearch's onSubmit).
 *
 * The accessible name lives in `title` (Constellation's documented
 * accessibility surface for IconButton) so screen readers still
 * announce "Search" even though the label is visually hidden.
 */
export default function SearchSubmit({ size, disabled, isPending }: SearchSubmitProps) {
  const tCommon = useTranslations('common');

  return (
    <IconButton
      // Mirrors legacy hotpads-web's testid so the shared Sonar monitors can assert one
      // selector against both apps. Only the `lg` search bar renders this, i.e. the homepage.
      data-testid="homepage-search-button"
      title={tCommon('search')}
      type="submit"
      shape="circle"
      size={size}
      tone="brand"
      emphasis="filled"
      disabled={disabled}
      aria-busy={isPending}
      icon={<LucideIcon icon={Search} />}
    />
  );
}
